import { Suspense, lazy, useState, useEffect, ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { User } from '../../types';
import { routeConfig } from '../../routes';

// Lazy-loaded Pages
const LoginPage = lazy(() => import('../Pages/LoginPage'));
const TestIntegration = lazy(() => import('../../pages/TestIntegration'));
const JoinerDashboard = lazy(() => import('../Pages/JoinerDashboard'));
const BuddyDashboard = lazy(() => import('../Pages/BuddyDashboard'));
const HRDashboard = lazy(() => import('../Pages/HRDashboard'));
const ManagerDashboard = lazy(() => import('../Pages/ManagerDashboard'));
const AdminDashboard = lazy(() => import('../Pages/AdminDashboard'));
const TeamLeadDashboard = lazy(() => import('../Pages/LeadDashboard.tsx'));
const TeamLeadTeamView = lazy(() => import('../Pages/LeadTeamView.tsx'));
const Tasks = lazy(() => import('../Pages/Tasks'));
const TaskDetailPage = lazy(() => import('../Pages/TaskDetailPage'));
const LabsPage = lazy(() => import('../Pages/LabsPage'));
const ProgressView = lazy(() => import('../Pages/ProgressView'));
const NotificationsView = lazy(() => import('../Pages/NotificationsView'));
const FeedbackView = lazy(() => import('../Pages/FeedbackView'));
const JoinersListView = lazy(() => import('../Pages/JoinersListView'));
const JoinerTaskTable = lazy(() => import('../Pages/JoinerTaskTable'));
const EditTaskPage = lazy(() => import('../Pages/EditTaskPage'));
const OnboardingRoadmap = lazy(() => import('../Pages/OnboardingRoadmap'));
const TaskReport = lazy(() => import('../Pages/TaskReport'));
const AchievementsView = lazy(() => import('../Pages/AchievementsView'));
const SubmitDocuments = lazy(() => import('../Pages/SubmitDocuments'));
const MessagingView = lazy(() => import('../Pages/MessagingView'));
const TeamManagement = lazy(() => import('../Pages/TeamManagement'));
const TeamsHRMS = lazy(() => import('../Pages/TeamsHRMS'));
const TeamDetailsPage = lazy(() => import('../Pages/TeamDetailsPage'));
const AnalyticsView = lazy(() => import('../Pages/AnalyticsView'));
const CertificatesView = lazy(() => import('../Pages/CertificatesView'));
const UnifiedReviews = lazy(() => import('../Pages/UnifiedReviews'));
const ScheduleDemo = lazy(() => import('../Pages/ScheduleDemo'));
const ReviewDetail = lazy(() => import('../Pages/ReviewDetail'));
const PreOnboarding = lazy(() => import('../Pages/PreOnboarding'));
const OnboardingVerification = lazy(() => import('../Pages/OnboardingVerification'));

const PerformanceQuarterlyView = lazy(() => import('../Pages/PerformanceQuarterlyView'));
const PerformanceMeetingDetail = lazy(() => import('../Pages/PerformanceMeetingDetail'));
const PerformanceDashboard = lazy(() => import('../Pages/PerformanceDashboard'));
const PerformanceGoalsView = lazy(() => import('../Pages/PerformanceGoalsView'));
const PerformanceFeedbackHistory = lazy(() => import('../Pages/PerformanceFeedbackHistory'));

const OverdueTask = lazy(() => import('../Pages/OverdueTask'));
const ProfilePage = lazy(() => import('../Pages/ProfilePage'));

// Components
import { ProtectedRoute } from './ProtectedRoute.tsx';
import NotFound from '../ui/NotFound.tsx';
import { LoadingScreen } from '../LoadingScreens/LoadingScreen.tsx';

interface AppRouterProps {
    currentUser: User | null;
    onLogin: (email: string, password: string) => Promise<boolean>;
}

// Custom loading wrapper that shows loading screen for 2 seconds
const LoadingWrapper = ({ children }: { children: ReactNode }) => {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    if (isLoading) {
        return <LoadingScreen />;
    }

    return <>{children}</>;
};

// Dashboard Route Factory
const DashboardRouteFactory = ({ currentUser }: { currentUser: User }) => {
    console.log('🎯 DashboardRouteFactory - Determining dashboard for user:', {
        name: currentUser.name,
        role: currentUser.role,
        team_role: currentUser.team_role,
        email: currentUser.email
    });

    // Handle team roles for non-HR users
    if (currentUser.role !== 'HR' && currentUser.role !== 'hr' && currentUser.team_role) {
        if (currentUser.team_role === 'TEAM_LEAD') {
            console.log('👔 Showing TeamLeadDashboard for team role:', currentUser.team_role);
            return <TeamLeadDashboard currentUser={currentUser} />;
        }
        if (currentUser.team_role === 'MEMBER') {
            if (currentUser.hasFullTimeTrack) {
                console.log('📊 Showing PerformanceDashboard for MEMBER with full-time track');
                return <PerformanceDashboard currentUser={currentUser} />;
            }
            console.log('🚧 Showing WorkingOnItDashboard for team role:', currentUser.team_role);
            return <WorkingOnItDashboard currentUser={currentUser} />;
        }
    }

    switch (currentUser.role) {
        case 'joiner':
            console.log('👋 Showing JoinerDashboard');
            return <JoinerDashboard currentUser={currentUser} />;
        case 'buddy':
            console.log('🤝 Showing BuddyDashboard');
            return <BuddyDashboard currentUser={currentUser} />;
        case 'HR':
        case 'hr':
            console.log('👑 Showing HRDashboard');
            return <HRDashboard currentUser={currentUser} />;
        case 'manager':
        case 'Manager':
        case 'MANAGER':
            console.log('👔 Showing ManagerDashboard');
            return <ManagerDashboard currentUser={currentUser} />;
        case 'ADMIN':
        case 'admin':
            console.log('🔧 Showing AdminDashboard');
            return <AdminDashboard currentUser={currentUser} />;
        default:
            console.log('❓ Unknown role, defaulting to JoinerDashboard');
            return <JoinerDashboard currentUser={currentUser} />;
    }
};

// Working on it dashboard for TEAM_LEAD/MEMBER team roles
const WorkingOnItDashboard = ({ currentUser }: { currentUser: User }) => {
    console.log(`Working on it - User: ${currentUser.name}, Team Role: ${currentUser.team_role}`);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white p-8 rounded-lg shadow text-center">
                    <div className="mb-6">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">
                            Welcome, {currentUser.name}!
                        </h1>
                        <p className="text-gray-600">
                            Team Role: <span className="font-semibold text-blue-600">{currentUser.team_role}</span>
                        </p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                        <h2 className="text-lg font-semibold text-yellow-800 mb-2">
                            🚧 Dashboard Under Development
                        </h2>
                        <p className="text-yellow-700">
                            We're working on creating a specialized dashboard for {currentUser.team_role?.toLowerCase()} roles.
                            Stay tuned for updates!
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div className="bg-gray-50 p-4 rounded">
                            <strong>Your Details:</strong>
                            <br />Email: {currentUser.email}
                            <br />Role: {currentUser.role}
                            <br />Team Role: {currentUser.team_role}
                            <br />ID: {currentUser.id}
                        </div>
                        <div className="bg-gray-50 p-4 rounded">
                            <strong>Console Output:</strong>
                            <br />✅ Authentication successful
                            <br />✅ Team role detected: {currentUser.team_role}
                            <br />🔄 Working on {currentUser.team_role} dashboard...
                        </div>
                    </div>


                </div>
            </div>
        </div>
    );
};

export const AppRouter = ({ currentUser, onLogin }: AppRouterProps) => {
    return (
        <LoadingWrapper>
            <Suspense fallback={<LoadingScreen />}>
                <Routes>
                    <Route path={routeConfig.login} element={<LoginPage onLogin={onLogin} />} />
                    <Route path={routeConfig.testIntegration} element={<TestIntegration />} />
                    <Route path="/" element={<Navigate to={routeConfig.dashboard} replace />} />

                    {/* Dashboard Route */}
                    <Route path={routeConfig.dashboard} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <DashboardRouteFactory currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    {/* Common Routes */}
                    <Route path={routeConfig.tasks} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <Tasks userRole={currentUser.role} />}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.taskDetail} element={
                        <ProtectedRoute currentUser={currentUser}>
                            <TaskDetailPage />
                        </ProtectedRoute>
                    } />

                    {/* Labs - unified page replacing ContentLibrary + LabsView */}
                    <Route path={routeConfig.labs} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <LabsPage userRole={currentUser.role} />}
                        </ProtectedRoute>
                    } />

                    {/* Redirects from old routes */}
                    <Route path="/content" element={<Navigate to="/labs" replace />} />
                    <Route path="/learning-hub" element={<Navigate to="/labs" replace />} />

                    <Route path={routeConfig.notifications} element={
                        <ProtectedRoute currentUser={currentUser}>
                            <NotificationsView />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.feedback} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <FeedbackView userRole={currentUser.role} />}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.messages} element={
                        <ProtectedRoute currentUser={currentUser}>
                            <MessagingView />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.profile} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <ProfilePage currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    {/* Joiner-specific routes */}
                    <Route path={routeConfig.progress} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['joiner']}>
                            <ProgressView />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.roadmap} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['joiner']}>
                            <OnboardingRoadmap />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.taskReport} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['joiner']}>
                            {currentUser && <TaskReport currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.achievements} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['joiner']}>
                            <AchievementsView />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.submitDocuments} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['joiner', 'buddy']}>
                            <SubmitDocuments />
                        </ProtectedRoute>
                    } />

                    {/* Buddy-specific routes */}

                    <Route path={routeConfig.reviews} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['buddy', 'admin']}>
                            <UnifiedReviews reviewType="buddy" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.buddyReviewDetail} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['buddy', 'admin']}>
                            <ReviewDetail reviewType="buddy" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.joinerReviewDetail} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['buddy', 'hr', 'manager', 'admin']}>
                            <ReviewDetail reviewType="buddy" />
                        </ProtectedRoute>
                    } />

                    {/* HR-specific routes */}
                    <Route path={routeConfig.analytics} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'manager', 'admin']} allowedTeamRoles={['team_lead']}>
                            {currentUser && <AnalyticsView currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.certificates} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'admin']}>
                            <CertificatesView />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.teamManagement} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'manager', 'admin']}>
                            <TeamManagement />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.teamsHRMS} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'manager', 'admin']}>
                            <TeamsHRMS />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.teamDetails} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'manager', 'admin']}>
                            <TeamDetailsPage />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.joiners} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['buddy', 'hr', 'manager', 'admin']}>
                            {currentUser && currentUser.role.toLowerCase() === 'hr' ? (
                                <JoinersListView userRole="hr" />
                            ) : (
                                <JoinersListView
                                    userRole={currentUser?.role || 'joiner'}
                                    buddyId={currentUser?.role === 'buddy' ? currentUser.id : undefined}
                                />
                            )}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.joinerTasks} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['buddy', 'hr', 'manager', 'admin']}>
                            <JoinerTaskTable />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.editTask} element={
                        <ProtectedRoute currentUser={currentUser}>
                            <EditTaskPage />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.hrReviews} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'admin']}>
                            <UnifiedReviews reviewType="hr" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.hrReviewDetail} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'admin']}>
                            <ReviewDetail reviewType="hr" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.scheduleDemo} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['buddy', 'hr', 'admin', 'joiner']} allowedTeamRoles={['team_lead', 'joiner']}>
                            {currentUser && <ScheduleDemo currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.preOnboarding} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'admin']}>
                            <PreOnboarding />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.verification} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['joiner', 'hr', 'manager', 'admin']}>
                            {currentUser && <OnboardingVerification currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.verificationDetail} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['hr', 'manager', 'admin']}>
                            {currentUser && <OnboardingVerification currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.managerReviews} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['manager', 'admin']}>
                            <UnifiedReviews reviewType="manager" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.managerReviewDetail} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['manager', 'admin']}>
                            <ReviewDetail reviewType="manager" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.adminReviews} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['admin']} allowAdminOverride={false}>
                            <UnifiedReviews reviewType="admin" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.adminReviewDetail} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['admin']} allowAdminOverride={false}>
                            <ReviewDetail reviewType="admin" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.teamLeadReviews} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['admin']} allowedTeamRoles={['team_lead']}>
                            <UnifiedReviews reviewType="team-lead" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.teamLeadReviewDetail} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['admin']} allowedTeamRoles={['team_lead']}>
                            <ReviewDetail reviewType="team-lead" />
                        </ProtectedRoute>
                    } />

                    <Route path={routeConfig.overdueTasks} element={
                        <ProtectedRoute currentUser={currentUser} allowedRoles={['buddy', 'hr']} allowedTeamRoles={['team_lead']}>
                            {currentUser && (
                                <OverdueTask
                                    userRole={currentUser.role}
                                    userTeamRole={currentUser.team_role}
                                    userId={currentUser.id}
                                />
                            )}
                        </ProtectedRoute>
                    } />

                    {/* Team Lead-specific routes */}
                    <Route path={routeConfig.myTeam} element={
                        <ProtectedRoute currentUser={currentUser} allowedTeamRoles={['team_lead']} allowAdminOverride={false}>
                            {currentUser && <TeamLeadTeamView currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    {/* Full-time performance track */}
                    <Route path={routeConfig.performance} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <PerformanceDashboard currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />
                    <Route
                        path={routeConfig.performanceMeetings}
                        element={<Navigate to={routeConfig.performanceQuarterly} replace />}
                    />
                    <Route path={routeConfig.performanceQuarterly} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <PerformanceQuarterlyView currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />
                    <Route path={routeConfig.performanceMeetingDetail} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <PerformanceMeetingDetail currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />
                    <Route path={routeConfig.performanceGoals} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <PerformanceGoalsView currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />
                    <Route path={routeConfig.performanceFeedback} element={
                        <ProtectedRoute currentUser={currentUser}>
                            {currentUser && <PerformanceFeedbackHistory currentUser={currentUser} />}
                        </ProtectedRoute>
                    } />

                    {/* Catch all unmatched routes */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </LoadingWrapper>
    );
};
